import { FC, useEffect, useState } from 'react';
import {
  Autosuggest,
	Container,
	FormField,
	SpaceBetween,
  ColumnLayout,
  Input,
  Tiles
} from '@cloudscape-design/components';
import { useForm, Controller, RegisterOptions } from 'react-hook-form';
import { useNewPlatformFormStore } from '../../../../stores/platforms/newPlatformFormStore';
import { StepConfig } from '../steps_config';

const clusterTypeOptions = [
  {
    label: "Standard",
    value: "standard",
    description: "Manage your own node groups and machine type."
  },
  {
    label: "Autopilot",
    value: "autopilot",
    description: "Google-managed node groups and machine type."
  }
]

const clusterSizeOptions = [
  {
    label: "Small",
    value: "small",
    description: "Best for development, testing, or low-traffic workloads with minimal capacity needs."
  },
  {
    label: "Medium",
    value: "medium",
    description: "Balanced choice for staging or moderate workloads that need more headroom."
  },
  {
    label: "Large",
    value: "large",
    description: "Suited for production or high-traffic workloads that need higher capacity."
  },
]

const getMachineTypeDescription = (machineType: string) => {
  const [, profile, cpu] = machineType.split('-')
  const vcpu = Number(cpu)

  if (!vcpu) {
    return 'Shared-core E2 machine type'
  }

  const memoryByProfile = {
    standard: vcpu * 4,
    highmem: vcpu * 8,
    highcpu: vcpu
  }[profile]

  return `${vcpu} vCPU, ${memoryByProfile} GB memory`
}

const getMachineTypeOptions = (machineTypes: Array<string>) =>
  machineTypes.map((machineType) => ({
    label: machineType,
    value: machineType,
    description: getMachineTypeDescription(machineType)
  }))

const machineTypeOptions = [
  {
    label: 'E2',
    options: getMachineTypeOptions([
      'e2-standard-2',
      'e2-standard-4',
      'e2-standard-8',
      'e2-standard-16',
      'e2-standard-32'
    ])
  },
  {
    label: 'N2',
    options: getMachineTypeOptions([
      'n2-standard-2',
      'n2-standard-4',
      'n2-standard-8',
      'n2-standard-16',
      'n2-standard-32',
      'n2-standard-48',
      'n2-standard-64',
      'n2-standard-80',
      'n2-standard-96',
      'n2-standard-128'
    ])
  },
  {
    label: 'N2D',
    options: getMachineTypeOptions([
      'n2d-standard-2',
      'n2d-standard-4',
      'n2d-standard-8',
      'n2d-standard-16',
      'n2d-standard-32',
      'n2d-standard-48',
      'n2d-standard-64',
      'n2d-standard-80',
      'n2d-standard-96',
      'n2d-standard-128',
      'n2d-standard-224'
    ])
  }
]

const standardMachineTypePattern = /^[a-z][a-z0-9]*-standard-[1-9]\d*(?:-[a-z0-9]+)?$/

const clusterStrategyFields: Array<StepConfig> = [
  {
    label: "Minimum Node",
    fieldName: "minNode",
    description: 'Minimum number of nodes',
    placeholder: "1",
    type: 'number',
  },
  {
    label: "Maximum Node",
    fieldName: "maxNode",
    description: 'Maximum number of nodes',
    placeholder: "10",
    type: 'number',
  },
  {
    label: "Machine Type",
    fieldName: "machineType",
    description: 'Select or enter a custom machine type',
    placeholder: "Select or enter machine type",
    type: 'text',
  },
]

const getClusterStrategyFieldTypeRules = (
  fieldType: string,
  fieldName: string,
): RegisterOptions => {
  switch (fieldType) {
    case 'number': 
      if (fieldName === "minNode") {
        return {
          required: "Please enter a value",
          min: {
            value: 1,
            message: "Minimum value is 1"
          }
        }
      }
      if (fieldName === "maxNode") {
        return {
          required: "Please enter a value",
          min: {
            value: 1,
            message: "Minimum value is 1"
          },
          validate: (maxNode, formValues) => {
            const minNode = Number(formValues.minNode)
            const maxNodeValue = Number(maxNode)

            if (!formValues.minNode || !maxNode) {
              return true
            }

            return (
              maxNodeValue >= minNode ||
              "Maximum node must be greater than or equal to minimum node"
            )
          },
          deps: ["minNode"]
        }
      }
      return {required: true}
    case 'text':
      if (fieldName === "machineType") {
        return {
          required: "Please enter a machine type",
          validate: (machineType) => {
            const machineTypeValue = String(machineType ?? '').trim()

            return (
              standardMachineTypePattern.test(machineTypeValue) ||
              "Use a standard machine type format like e2-standard-2 or n2d-standard-4"
            )
          }
        }
      }
      return {required: true}
    default:
      return {required: true}
  }
}

type GcpClusterStrategyProps = {
  registerValidation?: (validate: () => Promise<boolean>) => void
}

const GcpClusterStrategy: FC<GcpClusterStrategyProps> = ({ registerValidation }) => {

  const [popFields, setPopFields] = useState(false)

  const {
    formValues,
    actions: { setFormValues, setFormValidity }
  } = useNewPlatformFormStore();

  const {
    control,
    trigger,
    formState: {isValid}
  } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: formValues
  })

  useEffect(() => {
    if (formValues?.platformType?.value === 'standard') {
      setPopFields(true)
    } else {
      setPopFields(false)
    }
  }, [formValues?.platformType?.value]) 

  useEffect(() => {
    console.log('Form validity changed:', isValid)
    setFormValidity(isValid)
  }, [isValid, setFormValidity])

  useEffect(() => {
    registerValidation?.(trigger)
  }, [registerValidation, trigger])
  
  return (
    <Container>
      <SpaceBetween direction='vertical' size='l'>
        <Controller
          name="platformType"
          control={control}
          rules={{required: 'Please select a cluster type'}}
          render={({
            field: {onChange, value},
            fieldState: {error}
          }) => (
            <FormField
            label="Cluster Type"
            description="Select a cluster type between standard and autopilot"
            errorText={error?.message}
            stretch
            >
              <Tiles
                value={value?.value || ''}
                columns={2}
                items={clusterTypeOptions}
                onChange={({detail}) => {
                  const selectedOption = clusterTypeOptions.find(
                    (option) => option.value === detail.value
                  )

                  onChange(selectedOption)
                  setFormValues("platformType", selectedOption)
                }}
              />

            </FormField>
          )}  
        />
        {popFields && (
          <ColumnLayout columns={3}>
            {clusterStrategyFields.map((field: StepConfig) => {
              return(
                <Controller
                  name={field.fieldName}
                  control={control}
                  key={field.fieldName}
                  rules={getClusterStrategyFieldTypeRules(field.type, field.fieldName)}
                  render={({
                    field: {name, onChange, value},
                    fieldState: {error}
                  }) => (
                    <FormField
                      label={field.label}
                      description={field.description}
                      errorText={error?.message}
                      stretch
                    >
                      {(field.fieldName === 'machineType') && (
                        <Autosuggest
                          value={value ?? ''}
                          options={machineTypeOptions}
                          placeholder={field.placeholder}
                          ariaLabel="Machine type"
                          empty="No matching standard machine types. Enter a custom machine type."
                          onChange={({detail}) => {
                            onChange(detail.value)
                            setFormValues(
                              field.fieldName,
                              detail.value
                            )
                          }}
                        />
                      )}
                      {(field.type === 'number') && (
                        <Input
                          name={name}
                          placeholder={field.placeholder}
                          value={String(value ?? '')}
                          type={field.type}
                          onChange={({detail}) => {
                            onChange(detail.value)
                            setFormValues(
                              field.fieldName,
                              detail.value === '' ? '' : +detail.value
                            )
                          }}  
                        />
                      )}

                    </FormField>
                  )}
                />
              )
            })}
          </ColumnLayout>
        )}
        <Controller
          name='platformSize'
          control={control}
          rules={{required: "Please select a cluster size"}}
          render={({
            field: {onChange,value},
            fieldState: {error}
          }) => (
            <FormField
            label="Cluster Size"
            description="Choose the baseline capacity profile for this cluster."
            errorText={error?.message}
            stretch
            >
              <Tiles
              value={value?.value || ''}
              columns={3}
              items={clusterSizeOptions}
              onChange={({detail}) => {
                const selectedOption = clusterSizeOptions.find(
                  (option) => option.value === detail.value
                )

                onChange(selectedOption)
                setFormValues("platformSize", selectedOption)
                }}/>
            </FormField>
          )}
        />
      </SpaceBetween>
    </Container>
  )
}

export default GcpClusterStrategy
